import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./CashFlow.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

const ENTRY_SHOPS = SHOPS.filter((s) => s !== "ALL");

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function getRemaining(row) {
  return Math.max(
    Number(row.cod_amount || 0) - Number(row.settled_amount || 0),
    0,
  );
}

function getCodStatus(row) {
  const remaining = getRemaining(row);
  const settled = Number(row.settled_amount || 0);

  if (remaining <= 0) return "SETTLED";
  if (settled > 0) return "PARTIAL";
  return "PENDING";
}

export default function CashFlow() {
  const [summary, setSummary] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [codRows, setCodRows] = useState([]);

  const [shop, setShop] = useState("ALL");
  const [date, setDate] = useState(todayKey());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settlingId, setSettlingId] = useState("");

  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseSaving, setExpenseSaving] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [settleModal, setSettleModal] = useState({
    open: false,
    row: null,
    amount: "",
    batchNo: "",
    paymentMethod: "Bank",
  });

  const [form, setForm] = useState({
    shopCode: "MDYHT",
    ledgerDate: todayKey(),
    direction: "IN",
    entryType: "MANUAL_CASH_IN",
    paymentMethod: "Cash",
    amount: "",
    referenceNo: "",
    note: "",
  });

  const [expenseForm, setExpenseForm] = useState({
    shopCode: "MDYHT",
    expenseDate: todayKey(),
    paymentMethod: "Cash",
    amount: "",
    categoryName: "Delivery Expense",
    referenceNo: "",
    note: "",
  });

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function updateExpenseForm(field, value) {
    setExpenseForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function openSettleModal(row) {
    const remaining = getRemaining(row);

    setSettleModal({
      open: true,
      row,
      amount: String(remaining),
      batchNo: `REX-${todayKey()}`,
      paymentMethod: "Bank",
    });
  }

  function closeSettleModal() {
    if (settlingId) return;

    setSettleModal({
      open: false,
      row: null,
      amount: "",
      batchNo: "",
      paymentMethod: "Bank",
    });
  }

  function updateSettleModal(field, value) {
    setSettleModal((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function loadData() {
    try {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      const summaryRes = await fetch(
        `${API_BASE}/api/erp/cash-flow-summary?shop=${shop}&date=${date}`,
      );
      const summaryData = await summaryRes.json();

      if (!summaryRes.ok || summaryData.success === false) {
        throw new Error(summaryData.message || "Failed to load cash summary");
      }

      const ledgerRes = await fetch(
        `${API_BASE}/api/erp/cash-ledger?shop=${shop}&date=${date}`,
      );
      const ledgerData = await ledgerRes.json();

      if (!ledgerRes.ok || ledgerData.success === false) {
        throw new Error(ledgerData.message || "Failed to load cash ledger");
      }

      const codRes = await fetch(
        `${API_BASE}/api/erp/cod-receivables?shop=${shop}&date=${date}`,
      );
      const codData = await codRes.json();

      if (!codRes.ok || codData.success === false) {
        throw new Error(codData.message || "Failed to load COD receivables");
      }

      const categoryRes = await fetch(`${API_BASE}/api/erp/expense-categories`);
      const categoryData = await categoryRes.json();

      if (!categoryRes.ok || categoryData.success === false) {
        throw new Error(categoryData.message || "Failed to load expense categories");
      }

      setSummary(summaryData);
      setLedgerRows(ledgerData.rows || []);
      setCodRows(codData.rows || []);
      setExpenseCategories(categoryData.rows || []);
    } catch (err) {
      setError(err.message || "Cash flow loading failed");
    } finally {
      setLoading(false);
    }
  }

  async function submitCashEntry(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccessMsg("");

      const amount = Number(form.amount || 0);

      if (!form.shopCode) {
        throw new Error("Shop required");
      }

      if (amount <= 0) {
        throw new Error("Amount must be greater than 0");
      }

      const payload = {
        shopCode: form.shopCode,
        ledgerDate: form.ledgerDate || date,
        direction: form.direction,
        entryType: form.entryType,
        paymentMethod: form.paymentMethod,
        amount,
        referenceNo: form.referenceNo,
        note: form.note,
        createdBy: "admin",
      };

      const res = await fetch(`${API_BASE}/api/erp/cash-ledger`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save cash entry");
      }

      setSuccessMsg("Cash entry saved successfully.");

      setForm((prev) => ({
        ...prev,
        amount: "",
        referenceNo: "",
        note: "",
      }));

      await loadData();
    } catch (err) {
      setError(err.message || "Cash entry save failed");
    } finally {
      setSaving(false);
    }
  }

  async function submitSettleCod(e) {
    e.preventDefault();

    try {
      setError("");
      setSuccessMsg("");

      const row = settleModal.row;
      const settledAmount = Number(settleModal.amount || 0);

      if (!row?.id) {
        throw new Error("COD row not found");
      }

      if (settledAmount <= 0) {
        throw new Error("Settled amount must be greater than 0");
      }

      const remaining = getRemaining(row);

      if (settledAmount > remaining) {
        throw new Error(`Amount cannot be greater than remaining ${formatMoney(remaining)} Ks`);
      }

      setSettlingId(row.id);

      const res = await fetch(
        `${API_BASE}/api/erp/cod-receivables/${row.id}/settle`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            settledAmount,
            settlementBatchNo: settleModal.batchNo,
            paymentMethod: settleModal.paymentMethod,
            note: "Royal Express COD settled from ERP Cash Flow page",
            createdBy: "admin",
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to settle COD");
      }

      setSuccessMsg(`COD settled successfully: ${row.voucher_no}`);
      closeSettleModal();
      await loadData();
    } catch (err) {
      setError(err.message || "COD settlement failed");
    } finally {
      setSettlingId("");
    }
  }

  async function submitManualExpense(e) {
    e.preventDefault();

    try {
      setExpenseSaving(true);
      setError("");
      setSuccessMsg("");

      const amount = Number(expenseForm.amount || 0);

      if (!expenseForm.shopCode) {
        throw new Error("Shop required");
      }

      if (amount <= 0) {
        throw new Error("Expense amount must be greater than 0");
      }

      if (!expenseForm.categoryName) {
        throw new Error("Expense category required");
      }

      const res = await fetch(`${API_BASE}/api/erp/manual-expense`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shopCode: expenseForm.shopCode,
          expenseDate: expenseForm.expenseDate || date,
          paymentMethod: expenseForm.paymentMethod,
          amount,
          categoryName: expenseForm.categoryName,
          referenceNo: expenseForm.referenceNo,
          note: expenseForm.note,
          createdBy: "admin",
        }),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save expense");
      }

      setSuccessMsg("Manual expense saved successfully.");

      setExpenseForm((prev) => ({
        ...prev,
        amount: "",
        referenceNo: "",
        note: "",
      }));

      await loadData();
    } catch (err) {
      setError(err.message || "Manual expense save failed");
    } finally {
      setExpenseSaving(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, date]);

  const cash = summary?.cash || {};
  const cod = summary?.cod || {};
  const accounts = summary?.accounts || {};

  const pendingCodRows = codRows.filter((row) => getCodStatus(row) !== "SETTLED");
  const settledCodRows = codRows.filter((row) => getCodStatus(row) === "SETTLED");

  const paymentSummaryRows = useMemo(() => {
    const map = new Map();

    for (const row of ledgerRows) {
      const payment = row.payment_method || "Unknown";
      const amount = Number(row.amount || 0);

      if (!map.has(payment)) {
        map.set(payment, {
          payment,
          cashIn: 0,
          cashOut: 0,
          net: 0,
          count: 0,
        });
      }

      const item = map.get(payment);

      if (row.direction === "OUT") {
        item.cashOut += amount;
      } else {
        item.cashIn += amount;
      }

      item.net = item.cashIn - item.cashOut;
      item.count += 1;
    }

    return Array.from(map.values()).sort(
      (a, b) => Math.abs(b.net) - Math.abs(a.net),
    );
  }, [ledgerRows]);
  const expenseCategorySummaryRows = useMemo(() => {
    const map = new Map();

    for (const cat of expenseCategories) {
      map.set(cat.category_name, {
        categoryName: cat.category_name,
        amount: 0,
        count: 0,
      });
    }

    for (const row of ledgerRows) {
      if (row.direction !== "OUT") continue;

      const categoryName =
        row.expense_category_name ||
        (row.entry_type === "POS_RETURN" ? "Refund / Return" : "Other Expense");

      if (!map.has(categoryName)) {
        map.set(categoryName, {
          categoryName,
          amount: 0,
          count: 0,
        });
      }

      const item = map.get(categoryName);
      item.amount += Number(row.amount || 0);
      item.count += 1;
    }

    return Array.from(map.values())
      .filter((row) => row.amount > 0 || row.count > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [ledgerRows, expenseCategories]);

  return (
    <Layout title="Cash Flow">
      <div className="cashFlow">
        <div className="cashFlow__hero">
          <div>
            <div className="cashFlow__eyebrow">ERP Finance Control</div>
            <h1>Cash Flow Dashboard</h1>
            <p>
              Monitor company cash, payment accounts, and Royal Express COD
              receivables.
            </p>
          </div>

          <div className="cashFlow__filters">
            <input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                updateForm("ledgerDate", e.target.value);
                updateExpenseForm("expenseDate", e.target.value);
              }}
            />

            <LocationSelect
              value={shop}
              onChange={setShop}
              type="SHOP"
              includeAll={true}
              allLabel="All Shops"
            />

            <button onClick={loadData} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && <div className="cashFlow__error">{error}</div>}
        {successMsg && <div className="cashFlow__success">{successMsg}</div>}

        <div className="cashFlow__cards">
          <div className="cashCard cashCard--in">
            <span>Cash In</span>
            <strong>{formatMoney(cash.cashIn)} Ks</strong>
            <small>Company received money</small>
          </div>

          <div className="cashCard cashCard--out">
            <span>Cash Out</span>
            <strong>{formatMoney(cash.cashOut)} Ks</strong>
            <small>Expense / refund / transfer out</small>
          </div>

          <div className="cashCard cashCard--net">
            <span>Net Cash</span>
            <strong>{formatMoney(cash.netCash)} Ks</strong>
            <small>Cash in minus cash out</small>
          </div>

          <div className="cashCard cashCard--cod">
            <span>Royal Express COD Pending</span>
            <strong>{formatMoney(cod.pending)} Ks</strong>
            <small>Money not received yet</small>
          </div>
        </div>

        <div className="cashFlow__grid">
          <div className="cashPanel">
            <h3>Manual Cash Entry</h3>

            <form className="cashForm" onSubmit={submitCashEntry}>
              <div className="cashForm__row">
                <label>
                  Shop
                  <select
                    value={form.shopCode}
                    onChange={(e) => updateForm("shopCode", e.target.value)}
                  >
                    {ENTRY_SHOPS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Date
                  <input
                    type="date"
                    value={form.ledgerDate}
                    onChange={(e) => updateForm("ledgerDate", e.target.value)}
                  />
                </label>
              </div>

              <div className="cashForm__row">
                <label>
                  Direction
                  <select
                    value={form.direction}
                    onChange={(e) => {
                      const direction = e.target.value;
                      updateForm("direction", direction);
                      updateForm(
                        "entryType",
                        direction === "IN"
                          ? "MANUAL_CASH_IN"
                          : "MANUAL_CASH_OUT",
                      );
                    }}
                  >
                    <option value="IN">Cash In</option>
                    <option value="OUT">Cash Out</option>
                  </select>
                </label>

                <label>
                  Payment
                  <select
                    value={form.paymentMethod}
                    onChange={(e) =>
                      updateForm("paymentMethod", e.target.value)
                    }
                  >
                    <option value="Cash">Cash</option>
                    <option value="MMQR">MMQR</option>
                    <option value="KPay">KPay</option>
                    <option value="Wave">Wave</option>
                    <option value="Bank">Bank</option>
                    <option value="Special">Special Account</option>
                  </select>
                </label>
              </div>

              <label>
                Amount
                <input
                  type="number"
                  min="1"
                  value={form.amount}
                  onChange={(e) => updateForm("amount", e.target.value)}
                  placeholder="Enter amount"
                />
              </label>

              <label>
                Reference No
                <input
                  value={form.referenceNo}
                  onChange={(e) => updateForm("referenceNo", e.target.value)}
                  placeholder="Example: CASH-001"
                />
              </label>

              <label>
                Note
                <textarea
                  value={form.note}
                  onChange={(e) => updateForm("note", e.target.value)}
                  placeholder="Write note"
                />
              </label>

              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Cash Entry"}
              </button>
            </form>
          </div>

          <div className="cashPanel">
            <h3>Royal Express Overview</h3>
            <div className="cashRows">
              <div>
                <span>Total COD Count</span>
                <strong>{formatMoney(cod.count)}</strong>
              </div>
              <div>
                <span>Total COD Amount</span>
                <strong>{formatMoney(cod.total)} Ks</strong>
              </div>
              <div>
                <span>Pending Amount</span>
                <strong>{formatMoney(cod.pending)} Ks</strong>
              </div>
              <div>
                <span>Settled Amount</span>
                <strong>{formatMoney(cod.settled)} Ks</strong>
              </div>
              <div>
                <span>Pending Rows</span>
                <strong>{formatMoney(pendingCodRows.length)}</strong>
              </div>
              <div>
                <span>Settled Rows</span>
                <strong>{formatMoney(settledCodRows.length)}</strong>
              </div>
              <div>
                <span>Payment Accounts</span>
                <strong>{formatMoney(accounts.count)}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="cashFlow__grid cashFlow__grid--expense">
          <div className="cashPanel">
            <h3>Manual Expense Entry</h3>

            <form className="cashForm" onSubmit={submitManualExpense}>
              <div className="cashForm__row">
                <label>
                  Shop
                  <select
                    value={expenseForm.shopCode}
                    onChange={(e) =>
                      updateExpenseForm("shopCode", e.target.value)
                    }
                  >
                    {ENTRY_SHOPS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Date
                  <input
                    type="date"
                    value={expenseForm.expenseDate}
                    onChange={(e) =>
                      updateExpenseForm("expenseDate", e.target.value)
                    }
                  />
                </label>
              </div>

              <div className="cashForm__row">
                <label>
                  Expense Category
                  <select
                    value={expenseForm.categoryName}
                    onChange={(e) =>
                      updateExpenseForm("categoryName", e.target.value)
                    }
                  >
                    {expenseCategories.length === 0 ? (
                      <option value="Other Expense">Other Expense</option>
                    ) : (
                      expenseCategories.map((cat) => (
                        <option key={cat.id} value={cat.category_name}>
                          {cat.category_name}
                        </option>
                      ))
                    )}
                  </select>
                </label>

                <label>
                  Payment
                  <select
                    value={expenseForm.paymentMethod}
                    onChange={(e) =>
                      updateExpenseForm("paymentMethod", e.target.value)
                    }
                  >
                    <option value="Cash">Cash</option>
                    <option value="MMQR">MMQR</option>
                    <option value="KPay">KPay</option>
                    <option value="Wave">Wave</option>
                    <option value="Bank">Bank</option>
                    <option value="Special">Special Account</option>
                  </select>
                </label>
              </div>

              <label>
                Amount
                <input
                  type="number"
                  min="1"
                  value={expenseForm.amount}
                  onChange={(e) =>
                    updateExpenseForm("amount", e.target.value)
                  }
                  placeholder="Enter expense amount"
                />
              </label>

              <label>
                Reference No
                <input
                  value={expenseForm.referenceNo}
                  onChange={(e) =>
                    updateExpenseForm("referenceNo", e.target.value)
                  }
                  placeholder="Example: EXP-001"
                />
              </label>

              <label>
                Note
                <textarea
                  value={expenseForm.note}
                  onChange={(e) => updateExpenseForm("note", e.target.value)}
                  placeholder="Write expense note"
                />
              </label>

              <button type="submit" disabled={expenseSaving}>
                {expenseSaving ? "Saving..." : "Save Expense"}
              </button>
            </form>
          </div>

          <div className="cashPanel">
            <h3>Expense Category Guide</h3>

            <div className="cashRows">
              <div>
                <span>Delivery Expense</span>
                <strong>Royal / courier / delivery cost</strong>
              </div>
              <div>
                <span>Staff Expense</span>
                <strong>Staff allowance / support</strong>
              </div>
              <div>
                <span>Packaging Expense</span>
                <strong>Bag / box / packing items</strong>
              </div>
              <div>
                <span>Bank Charges</span>
                <strong>Bank fee / transaction fee</strong>
              </div>
              <div>
                <span>Other Expense</span>
                <strong>General cash out</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="cashPanel cashPanel--table">
          <div className="cashPanel__tableHead">
            <div>
              <h3>Payment Method Breakdown</h3>
              <p>Today cash movement by Cash, MMQR, KPay, Wave, Bank and Special Account.</p>
            </div>
          </div>

          <div className="cashTableWrap">
            <table className="cashTable">
              <thead>
                <tr>
                  <th>Payment Method</th>
                  <th>Cash In</th>
                  <th>Cash Out</th>
                  <th>Net</th>
                  <th>Entries</th>
                </tr>
              </thead>

              <tbody>
                {paymentSummaryRows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="cashTable__empty">
                      No payment method data.
                    </td>
                  </tr>
                ) : (
                  paymentSummaryRows.map((row) => (
                    <tr key={row.payment}>
                      <td>
                        <span className="cashPaymentName">{row.payment}</span>
                      </td>
                      <td>{formatMoney(row.cashIn)} Ks</td>
                      <td>{formatMoney(row.cashOut)} Ks</td>
                      <td>
                        <strong>{formatMoney(row.net)} Ks</strong>
                      </td>
                      <td>{formatMoney(row.count)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="cashPanel cashPanel--table">
          <div className="cashPanel__tableHead">
            <div>
              <h3>Royal Express COD List</h3>
              <p>Pending COD can be settled when Royal Express transfers money.</p>
            </div>
          </div>

          <div className="cashTableWrap">
            <table className="cashTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Shop</th>
                  <th>Voucher</th>
                  <th>Waybill</th>
                  <th>Customer</th>
                  <th>COD Amount</th>
                  <th>Settled</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {codRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="cashTable__empty">
                      No Royal Express COD data.
                    </td>
                  </tr>
                ) : (
                  codRows.map((row) => {
                    const status = getCodStatus(row);
                    const remaining = getRemaining(row);

                    return (
                      <tr key={row.id}>
                        <td>{String(row.sale_date || "").slice(0, 10)}</td>
                        <td>{row.shop_code}</td>
                        <td>{row.voucher_no}</td>
                        <td>{row.waybill_no || "-"}</td>
                        <td>
                          <div className="cashCustomer">
                            <strong>{row.customer_name || "-"}</strong>
                            <span>{row.customer_phone || ""}</span>
                          </div>
                        </td>
                        <td>{formatMoney(row.cod_amount)} Ks</td>
                        <td>{formatMoney(row.settled_amount)} Ks</td>
                        <td>{formatMoney(remaining)} Ks</td>
                        <td>
                          <span
                            className={
                              status === "SETTLED"
                                ? "cashBadge cashBadge--in"
                                : status === "PARTIAL"
                                  ? "cashBadge cashBadge--partial"
                                  : "cashBadge cashBadge--pending"
                            }
                          >
                            {status}
                          </span>
                        </td>
                        <td>
                          {status === "SETTLED" ? (
                            <span className="cashDoneText">Done</span>
                          ) : (
                            <button
                              className="cashSettleBtn"
                              onClick={() => openSettleModal(row)}
                              disabled={settlingId === row.id}
                            >
                              {settlingId === row.id ? "Settling..." : "Settle"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="cashPanel cashPanel--table">
          <div className="cashPanel__tableHead">
            <div>
              <h3>Expense Category Breakdown</h3>
              <p>Today manual expenses, refunds and cash-out records grouped by category.</p>
            </div>
          </div>

          <div className="cashTableWrap">
            <table className="cashTable">
              <thead>
                <tr>
                  <th>Expense Category</th>
                  <th>Total Cash Out</th>
                  <th>Entries</th>
                </tr>
              </thead>

              <tbody>
                {expenseCategorySummaryRows.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="cashTable__empty">
                      No expense category data.
                    </td>
                  </tr>
                ) : (
                  expenseCategorySummaryRows.map((row) => (
                    <tr key={row.categoryName}>
                      <td>
                        <span className="cashExpenseName">
                          {row.categoryName}
                        </span>
                      </td>
                      <td>
                        <strong className="cashExpenseAmount">
                          {formatMoney(row.amount)} Ks
                        </strong>
                      </td>
                      <td>{formatMoney(row.count)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="cashPanel cashPanel--table">
          <h3>Cash Ledger List</h3>

          <div className="cashTableWrap">
            <table className="cashTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Shop</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Direction</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Reference</th>
                  <th>Note</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {ledgerRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="cashTable__empty">
                      No cash ledger data.
                    </td>
                  </tr>
                ) : (
                  ledgerRows.map((row) => (
                    <tr key={row.id}>
                      <td>{String(row.ledger_date || "").slice(0, 10)}</td>
                      <td>{row.shop_code}</td>
                      <td>
                        <span className="cashTypeBadge">
                          {row.entry_type || "-"}
                        </span>
                      </td>
                      <td>
                        {row.expense_category_name ||
                          (row.entry_type === "POS_RETURN"
                            ? "Refund / Return"
                            : "-")}
                      </td>
                      <td>
                        <span
                          className={
                            row.direction === "IN"
                              ? "cashBadge cashBadge--in"
                              : "cashBadge cashBadge--out"
                          }
                        >
                          {row.direction}
                        </span>
                      </td>
                      <td>{row.payment_method}</td>
                      <td>{formatMoney(row.amount)} Ks</td>
                      <td>{row.reference_no || "-"}</td>
                      <td>{row.note || "-"}</td>
                      <td>{String(row.created_at || "").slice(0, 19)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {settleModal.open && settleModal.row && (
          <div className="cashModalOverlay" onMouseDown={closeSettleModal}>
            <div className="cashModal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="cashModal__head">
                <div>
                  <span>Royal Express Settlement</span>
                  <h3>{settleModal.row.voucher_no}</h3>
                </div>

                <button
                  type="button"
                  className="cashModal__close"
                  onClick={closeSettleModal}
                  disabled={Boolean(settlingId)}
                >
                  ×
                </button>
              </div>

              <div className="cashModal__summary">
                <div>
                  <span>COD Amount</span>
                  <strong>{formatMoney(settleModal.row.cod_amount)} Ks</strong>
                </div>
                <div>
                  <span>Already Settled</span>
                  <strong>{formatMoney(settleModal.row.settled_amount)} Ks</strong>
                </div>
                <div>
                  <span>Remaining</span>
                  <strong>{formatMoney(getRemaining(settleModal.row))} Ks</strong>
                </div>
              </div>

              <form className="cashModalForm" onSubmit={submitSettleCod}>
                <label>
                  Settlement Amount
                  <input
                    type="number"
                    min="1"
                    max={getRemaining(settleModal.row)}
                    value={settleModal.amount}
                    onChange={(e) => updateSettleModal("amount", e.target.value)}
                  />
                </label>

                <label>
                  Settlement Batch No
                  <input
                    value={settleModal.batchNo}
                    onChange={(e) => updateSettleModal("batchNo", e.target.value)}
                    placeholder="Example: REX-WEEK-001"
                  />
                </label>

                <label>
                  Payment Method
                  <select
                    value={settleModal.paymentMethod}
                    onChange={(e) =>
                      updateSettleModal("paymentMethod", e.target.value)
                    }
                  >
                    <option value="Bank">Bank</option>
                    <option value="Cash">Cash</option>
                    <option value="MMQR">MMQR</option>
                    <option value="KPay">KPay</option>
                    <option value="Wave">Wave</option>
                    <option value="Special">Special Account</option>
                  </select>
                </label>

                <div className="cashModal__actions">
                  <button
                    type="button"
                    className="cashModal__cancel"
                    onClick={closeSettleModal}
                    disabled={Boolean(settlingId)}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="cashModal__confirm"
                    disabled={Boolean(settlingId)}
                  >
                    {settlingId ? "Saving..." : "Confirm Settlement"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}






